// „Настани" от Маркиране — къде могат да седнат гостите (тестове:
// seatOptions.test.ts). Огледало на BE `service_seat_from_ordering.py`:
//
// - винаги: към МОЯ отворена сметка по масите, или НОВА моя маса (ако нямам своя);
// - чуждите сметки — само с `view_all_users_bills` (BE праща невидимите като `hidden`);
// - ОБЩА номерация: втора маса със заето име няма — чужда видима → само
//   закачане; чужда невидима и своя няма → спряно „Маса 2 е заета от Мария".
//
// Каквото и да е — сервитьорът ВИНАГИ потвърждава във втората стъпка.

import type { OpenTable, SeatableReservation, SeatableReservations } from "../../../../../api/reservations";
import {
  seatCheckOf,
  seatHintOf,
  upcomingActionsOf,
  type SeatHint,
  type UpcomingActions,
} from "../../../../reservations/logic/reservationActions";
import { isExpiredUpcoming } from "../../../../reservations/logic/tableStatus";

export type SeatOption =
  | { kind: "own"; bill: OpenTable }
  | { kind: "other"; bill: OpenTable }
  | { kind: "new"; table: string };

export type SeatOptions =
  | { kind: "blocked"; hint: SeatHint }
  | { kind: "choose"; options: SeatOption[] };

export const seatOptionKey = (option: SeatOption) =>
  option.kind === "new" ? "new" : `bill-${option.bill.billId}`;

const byOpenedAt = (left: OpenTable, right: OpenTable) =>
  Date.parse(left.openedAt) - Date.parse(right.openedAt);

// Причината на изключения „Настани" — общата с „Резервации", иначе своя:
// изтекъл час (до следващото опресняване резервацията още е в списъка),
// вече не предстояща (друг терминал я е настанил) или маси, изтрити от схемата.
const blockedHintOf = (
  reservation: SeatableReservation,
  available: UpcomingActions | null,
  nowMs: number,
): SeatHint => {
  const hint = available ? seatHintOf(available) : null;
  if (hint) return hint;
  if (isExpiredUpcoming(reservation, nowMs)) return { key: "seatReservation.expired", params: {} };
  if (reservation.status !== "upcoming") return { key: "seatReservation.notUpcoming", params: {} };
  return { key: "seatReservation.noTable", params: {} };
};

export const seatOptionsFor = (
  reservation: SeatableReservation,
  data: SeatableReservations,
  nowMs: number,
): SeatOptions => {
  // Спирачките по време/заетост са общите с модул „Резервации".
  const check = seatCheckOf(reservation, [...data.seatedNow, ...data.reservations], data.openTables);
  const available = upcomingActionsOf(reservation, nowMs, check);
  if (!available || available.seatBlock !== null) {
    return { kind: "blocked", hint: blockedHintOf(reservation, available, nowMs) };
  }

  const own = check.freeBills.filter((bill) => bill.waiterId === data.viewerId).sort(byOpenedAt);
  const others = check.freeBills.filter((bill) => bill.waiterId !== data.viewerId).sort(byOpenedAt);
  const visibleOthers = others.filter((bill) => !bill.hidden);
  const hiddenOther = others.find((bill) => bill.hidden);

  if (!data.personalNumbering && own.length === 0 && hiddenOther) {
    return {
      kind: "blocked",
      hint: hiddenOther.waiterName
        ? { key: "seatReservation.takenBy", params: { table: hiddenOther.tableName, waiter: hiddenOther.waiterName } }
        : { key: "seatReservation.takenByOther", params: { table: hiddenOther.tableName } },
    };
  }

  const canOpenNew = own.length === 0 && (data.personalNumbering || others.length === 0);
  return {
    kind: "choose",
    options: [
      ...own.map((bill): SeatOption => ({ kind: "own", bill })),
      ...visibleOthers.map((bill): SeatOption => ({ kind: "other", bill })),
      ...(canOpenNew ? [{ kind: "new", table: reservation.tableNames[0] } as SeatOption] : []),
    ],
  };
};

/**
 * Редовете „Към коя сметка" във въпроса — всички възможности, ако са поне
 * две, и при двата вида сметки: при „Отделна сметка за всяка маса" изборът
 * е главната сметка и на кого се отварят новите маси. [] — без избор.
 */
export const targetChoicesOf = (options: SeatOption[]): SeatOption[] => (options.length > 1 ? options : []);

/** ↑/↓ по редовете „Към коя сметка"; без избран — първият (↓) или последният (↑). */
export const nextTargetKey = (keys: string[], currentKey: string | null, delta: 1 | -1): string | null => {
  if (keys.length === 0) return currentKey;
  const index = currentKey === null ? -1 : keys.indexOf(currentKey);
  if (index === -1) return delta === 1 ? keys[0] : keys[keys.length - 1];
  return keys[Math.min(keys.length - 1, Math.max(0, index + delta))];
};
